import React, { useEffect, useCallback } from 'react';
import {
  BarChart3,
  Bed,
  Building2,
  Users,
  ExternalLink,
} from 'lucide-react';
import { useDormitoryData } from '../../../hooks/useDormitoryData';
import { useTabPersistence } from '../../../hooks/usePersistence';
import OverviewTab from './OverviewTab';
import LocationsTab from './LocationsTab';
import AssignmentsTab from './AssignmentsTab';

/**
 * Admin / Dormitory Management — read-only projection of the Dormitory
 * Portal.
 *
 * The HR Portal is the source of truth for employees; the Dormitory Portal
 * owns dormitory locations, rent, occupants, and movements. This page is
 * therefore a viewer over portal data, not an editor — every CRUD path goes
 * through "Open in Dormitory Portal" and lands the user on the portal.
 *
 * Three tabs:
 *   - Overview     : KPIs + cost-composition visualization + per-dorm table
 *   - Locations    : grid of dormitory cards (read-only)
 *   - Assignments  : table of resident → dormitory mappings (read-only)
 */
const DormitoryManagement = () => {
  const validTabs = ['overview', 'locations', 'assignments'];
  const [activeTab, setActiveTab] = useTabPersistence(
    'dormitory-management',
    'overview',
    validTabs,
  );

  const {
    portalOverview,
    portalResidents,
    loading,
    fetchPortalOverview,
    fetchPortalResidents,
  } = useDormitoryData();

  // Tabs share the same labour-cost call on the dorm-portal side (cached
  // for 15 min on the current month, 1 hour on past months), so loading
  // each tab is cheap. We deliberately fetch only what each tab needs.
  const loadData = useCallback(async () => {
    try {
      if (activeTab === 'overview' || activeTab === 'locations') {
        await fetchPortalOverview();
      } else if (activeTab === 'assignments') {
        await fetchPortalResidents();
      }
    } catch (err) {
      // Hook surfaces a toast for genuine failures; nothing more to do.
      // eslint-disable-next-line no-console
      console.error('Error loading dormitory data:', err);
    }
  }, [activeTab, fetchPortalOverview, fetchPortalResidents]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refreshOverview = useCallback(
    () => fetchPortalOverview({ refresh: 1 }),
    [fetchPortalOverview],
  );
  const refreshResidents = useCallback(
    () => fetchPortalResidents({ refresh: 1 }),
    [fetchPortalResidents],
  );

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'locations', name: 'Locations', icon: Building2 },
    { id: 'assignments', name: 'Assignments', icon: Users },
  ];

  // The portal URL is exposed by both endpoints; prefer whichever payload
  // we have so the header CTA stays available even if one tab errored.
  const dormPortalUrl =
    portalOverview?.dorm_portal_url || portalResidents?.dorm_portal_url || null;

  return (
    <div className="space-y-8">
      {/* Single row at every breakpoint — the trailing CTA stays inline with
          the title instead of dropping to its own full-width row on mobile.
          The label collapses to an icon-only button below sm. */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Bed className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Dormitory Management</h1>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">
            Worker housing — sourced live from the Dormitory Portal
          </p>
        </div>
        {dormPortalUrl && (
          <a
            href={dormPortalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 flex-shrink-0"
            title="Open the Dormitory Portal in a new tab"
          >
            <ExternalLink className="w-4 h-4" />
            <span className="hidden sm:inline">Open Dormitory Portal</span>
          </a>
        )}
      </div>

      {/* Tabs — DS canonical: tab-btn-admin + active, mobile scroll
          container per the design system rules. No inline red Tailwind. */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn-admin ${activeTab === tab.id ? 'active' : ''}`}
              title={tab.name}
            >
              <Icon className="w-4 h-4" />
              {tab.name}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'overview' && (
          <OverviewTab
            portalOverview={portalOverview}
            loading={loading}
            onRefresh={refreshOverview}
          />
        )}
        {activeTab === 'locations' && (
          <LocationsTab
            portalOverview={portalOverview}
            loading={loading}
            onRefresh={refreshOverview}
          />
        )}
        {activeTab === 'assignments' && (
          <AssignmentsTab
            portalResidents={portalResidents}
            loading={loading}
            onRefresh={refreshResidents}
          />
        )}
      </div>
    </div>
  );
};

export default DormitoryManagement;
